import { OfferForm } from "./offer.schema";
import { useFormContext } from "react-hook-form";
import { Card } from "@/src/components/wrappers/Card";
import { FormControl, FormField, FormItem, FormLabel } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Separator } from "../../../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Combobox } from "../../../components/ui/combobox";
import { fetch } from "../../../lib/fetch";
import { useEffect } from "react";
import { Broker } from "../model";
import { CurrencySelect } from "../../../components/CurrencySelect";
import { ScoreSelect } from "../../../components/ui/score-select";
import { useOffersStore } from "../../../stores/offersStore";
import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";
import { Label } from "@/src/components/ui/label";
import { logBasicFieldChange, useOfferCreatorBlurLogger } from "../offerCreatorLogs";

export function Description({ offerId }: { offerId?: string }) {
  const { setValue, getValues } = useFormContext<OfferForm>();
  const { yachtsFilters, setYachtsFilters } = useOffersStore();
  const { captureFieldFocus, logBasicFieldBlur } = useOfferCreatorBlurLogger();

  const date = getValues("description.enquiryDate");

  useEffect(() => {
    if (!date) {
      setValue("description.enquiryDate", new Date().toLocaleDateString());
    }
  }, []);

  return (
    <Card>
      <div className="flex flex-col">
        <h3 className="text-lg font-semibold col-span-full mb-4">Description</h3>
        <h5 className="text-sm col-span-full mb-6">Provide basic information about an Offer.</h5>
        <div className="flex gap-x-12">
          <div className="flex flex-col basis-[620px] gap-y-6">
            <FormField<OfferForm, "description.title">
              name="description.title"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormItem className="flex flex-col" required>
                  <FormLabel>Offer title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter offer title"
                      onFocus={() => captureFieldFocus("description.title", value)}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        logBasicFieldBlur({
                          offerId,
                          field: "description.title",
                          before: value,
                          after: e.currentTarget.value,
                        });
                      }}
                      value={value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField<OfferForm, "description.brand">
              name="description.brand"
              render={({ field: { onChange, value } }) => (
                <FormItem className="flex flex-col" required>
                  <FormLabel>Offer brand</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={value}
                      onValueChange={(next) => {
                        logBasicFieldChange({
                          offerId,
                          field: "description.brand",
                          before: value,
                          after: next,
                        });
                        onChange(next);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="helm" id="helm" />
                        <Label htmlFor="helm">HELM</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="anchor" id="anchor" />
                        <Label htmlFor="anchor">ANCHOR</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField<OfferForm, "description.intro">
              name="description.intro"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Intro</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={11}
                      maxLength={500}
                      placeholder="Enter intro"
                      onFocus={() => captureFieldFocus("description.intro", value)}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        logBasicFieldBlur({
                          offerId,
                          field: "description.intro",
                          before: value,
                          after: e.currentTarget.value,
                        });
                      }}
                      value={value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <Separator orientation="vertical" className="h-auto" />
          <div className="flex basis-[352px] flex-col gap-y-6">
            <FormField<OfferForm, "description.guests">
              name="description.guests"
              render={({ field }) => (
                <FormItem required>
                  <FormLabel>Guests</FormLabel>
                  <FormControl>
                    <ScoreSelect
                      min={1}
                      max={20}
                      {...field}
                      onChange={(value) => {
                        logBasicFieldChange({
                          offerId,
                          field: "description.guests",
                          before: field.value,
                          after: value,
                        });
                        field.onChange(value);
                        setYachtsFilters({ ...yachtsFilters, people: value });
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField<OfferForm, "description.currency">
              name="description.currency"
              render={({ field: { onChange, value } }) => (
                <FormItem className="flex flex-col" required>
                  <FormLabel>Currency</FormLabel>
                  <FormControl>
                    <CurrencySelect
                      onChange={(next) => {
                        logBasicFieldChange({
                          offerId,
                          field: "description.currency",
                          before: value,
                          after: next,
                        });
                        onChange(next);
                      }}
                      value={value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField<OfferForm, "description.charterType">
              name="description.charterType"
              render={({ field: { onChange, value } }) => (
                <FormItem className="flex flex-col" required>
                  <FormLabel>Charter type</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={(val) => {
                        logBasicFieldChange({
                          offerId,
                          field: "description.charterType",
                          before: value,
                          after: val,
                        });
                        if (val === "crewed") {
                          setValue("mailing.bookingUpdates", false);
                        }
                        onChange(val);
                      }}
                      defaultValue={value ? value : undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a charter type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="bareboat">Bareboat</SelectItem>
                        <SelectItem value="skippered">Skippered</SelectItem>
                        <SelectItem value="crewed">Crewed</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField<OfferForm, "description.enquiryDate">
              name="description.enquiryDate"
              render={() => {
                return (
                  <FormItem className="flex flex-col">
                    <FormLabel>Enquiry date</FormLabel>
                    <FormControl>
                      <Input
                        value={new Date().toLocaleDateString()}
                        disabled
                        className="border-none text-black !opacity-100 !px-0"
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField<OfferForm, "description.broker">
              name="description.broker"
              render={({ field }) => {
                return (
                  <FormItem required>
                    <FormLabel>Broker (Booking owner)</FormLabel>
                    <FormControl>
                      <Combobox<Broker>
                        className="w-full"
                        loadOptions={(q) =>
                          fetch<{ data: Broker[] }>(`/api/users/brokers${q ? `?filter=${q}` : ""}`).then((r) => r.data)
                        }
                        renderOption={(v) =>
                          (v as any)?.fullName || [v?.firstName, v?.lastName].filter(Boolean).join(" ") || ""
                        }
                        value={(field.value?.id ? field.value : null) as Broker}
                        onChange={(v) => {
                          logBasicFieldChange({
                            offerId,
                            field: "description.broker",
                            before: field.value,
                            after: v,
                          });
                          if (v && v.id !== field.value?.id) {
                            field.onChange(v);
                          } else {
                            field.onChange(null);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
